import { aiErrorText } from '../utils/aiErrors';
import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { Mic, Upload, CheckCircle, XCircle, Clock, Loader2, FileText, ClipboardCheck, Info } from 'lucide-react';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';
import { NewWorkLogModal } from './WorkLogsPage';
import type { VoiceUploadResult, VoiceWorkLogProposal, WorkLogDraftInitial } from '../types/voiceProposal';
import { draftFromVoiceEntry } from '../types/voiceProposal';
import StateBlock from '../components/ui/StateBlock';

export default function VoicePage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const user = useAuthStore((s) => s.user);
  // The AI result is a DRAFT: nothing is logged until the agent confirms it
  // in the regular work-log form (decisions 126/127).
  const [proposal, setProposal] = useState<{ data: VoiceWorkLogProposal; transcript?: string } | null>(null);
  const [draft, setDraft] = useState<WorkLogDraftInitial | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['voice-entries'],
    queryFn: () => api.getVoiceEntries({ limit: 50 }),
  });

  // D-234 (FIX-B36) - know BEFORE an upload whether the AI is connected. Unknown (loading /
  // the check failed) = the button stays as it was; the upload still answers AI_NOT_CONFIGURED.
  const aiStatus = useQuery({
    queryKey: ['voice-status'],
    queryFn: () => api.getVoiceStatus(),
    staleTime: 60_000,
  });
  const aiOff = aiStatus.data?.data?.configured === false;

  const uploadMutation = useMutation({
    mutationFn: (file: File) => api.uploadVoice(file),
    onMutate: () => {
      setProposal(null);
      setConfirmed(false);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['voice-entries'] });
      const result = (res?.data ?? null) as VoiceUploadResult | null;
      if (result?.proposal) {
        setProposal({ data: result.proposal, transcript: result.voice_entry?.transcription_text });
      }
    },
  });

  const entries = data?.data ?? [];

  const openDraft = (p: VoiceWorkLogProposal, ticket?: VoiceWorkLogProposal['candidate_tickets'][number]) => {
    setDraft({
      ticket: ticket ? { id: ticket.id, title: ticket.title, organization_name: ticket.organization_name } : undefined,
      summary: p.summary,
      durationMinutes: p.duration_minutes,
      statedStart: p.stated_start_time,
      statedEnd: p.stated_end_time,
      voiceEntryId: p.voice_entry_id,
    });
  };

  const handleFile = (file: File) => {
    setFileError(null);
    if (file.type.startsWith('audio/') || file.name.match(/\.(mp3|wav|ogg|webm|m4a)$/i)) {
      uploadMutation.mutate(file);
    } else {
      setFileError(t('voice.audioOnlyError'));
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  };

  const statusConfig: Record<string, { label: string; icon: any; color: string }> = {
    pending: { label: t('voice.pending'), icon: Clock, color: 'text-warning-700 bg-warning-100' },
    processing: { label: t('voice.processingStatus'), icon: Loader2, color: 'text-info-700 bg-info-100' },
    processed: { label: t('voice.completed'), icon: CheckCircle, color: 'text-success-700 bg-success-100' },
    failed: { label: t('voice.failed'), icon: XCircle, color: 'text-danger-700 bg-danger-100' },
  };

  return (
    <div className="space-y-5">
      <h1 className="page-title">{t('voice.title')}</h1>

      {/* D-234 - what this page is for, and (no AI) that it is not connected yet. */}
      <div className="flex gap-3 rounded-xl bg-accent-soft px-4 py-3 text-sm text-accent-soft-text" role="note">
        <Info size={18} className="mt-0.5 shrink-0" aria-hidden="true" />
        <div className="space-y-1.5">
          <p><span className="font-semibold">{t('voice.introLabel')}</span> {t('voice.intro')}</p>
          {aiOff && <p className="font-medium" role="status">{t('voice.aiNotConnected')}</p>}
        </div>
      </div>

      {/* Upload Area */}
      {/* The drop zone is a real button: click or Enter / Space opens the file picker. */}
      <button
        type="button"
        disabled={aiOff}
        aria-describedby={aiOff ? 'voice-ai-off' : undefined}
        className={`card block w-full border-2 border-dashed transition-colors ${
          aiOff ? 'cursor-not-allowed opacity-60 border-surface-300/60'
            : `cursor-pointer ${dragActive ? 'border-primary-400 bg-primary-50' : 'border-surface-300/60 hover:border-primary-300'}`
        }`}
        onDragOver={(e) => { e.preventDefault(); if (!aiOff) setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={(e) => { if (aiOff) { e.preventDefault(); return; } handleDrop(e); }}
        onClick={() => fileInputRef.current?.click()}
      >
        <span className="block text-center py-8">
          {uploadMutation.isPending ? (
            <>
              <Loader2 className="mx-auto text-accent-text mb-4 animate-spin" size={48} />
              <span className="block text-lg font-medium text-surface-900">{t('voice.processing')}</span>
              <span className="block text-sm text-surface-500 mt-1">{t('voice.processingNote')}</span>
            </>
          ) : (
            <>
              <Upload className="mx-auto text-surface-500 mb-4" size={48} />
              <span className="block text-lg font-medium text-surface-900">{t('voice.dropzone')}</span>
              <span className="block text-sm text-surface-500 mt-1">MP3, WAV, OGG, WEBM, M4A {t('voice.maxFileSize')}</span>
              {aiOff && <span id="voice-ai-off" className="block text-sm text-surface-700 mt-2">{t('voice.uploadDisabledAiOff')}</span>}
            </>
          )}
        </span>
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*"
        className="hidden"
        onChange={(e) => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}
      />

      {fileError && (
        <div className="card bg-danger-50 border border-danger-100">
          <div className="flex items-center gap-3">
            <XCircle className="text-danger-600" size={24} />
            <p className="text-danger-800">{fileError}</p>
          </div>
        </div>
      )}

      {confirmed && (
        <div className="card bg-success-50 border border-success-200">
          <div className="flex items-center gap-3">
            <CheckCircle className="text-success-600" size={24} />
            <p className="font-medium text-success-800">{t('voice.proposalConfirmed')}</p>
          </div>
        </div>
      )}

      {proposal && (
        <div className="card border border-primary-200 bg-primary-50 space-y-3">
          <div className="flex items-start gap-3">
            <ClipboardCheck className="text-accent-text shrink-0" size={24} />
            <div className="min-w-0">
              <p className="font-semibold text-surface-900">{t('voice.proposalTitle')}</p>
              <p className="text-sm text-surface-600">{t('voice.proposalSubtitle')}</p>
            </div>
          </div>

          {proposal.transcript && (
            <div>
              <p className="text-xs font-medium text-surface-500">{t('voice.proposalTranscript')}</p>
              <p className="text-sm text-surface-800" dir="auto">{proposal.transcript}</p>
            </div>
          )}
          {proposal.data.summary && (
            <div>
              <p className="text-xs font-medium text-surface-500">{t('voice.proposalSummary')}</p>
              <p className="text-sm text-surface-800" dir="auto">{proposal.data.summary}</p>
            </div>
          )}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-surface-600">
            <span>
              {proposal.data.duration_minutes
                ? t('voice.proposalDuration', { minutes: proposal.data.duration_minutes })
                : t('voice.proposalNoDuration')}
            </span>
            {proposal.data.stated_start_time && (
              <span>{t('voice.proposalStatedStart', { value: proposal.data.stated_start_time })}</span>
            )}
            {proposal.data.stated_end_time && (
              <span>{t('voice.proposalStatedEnd', { value: proposal.data.stated_end_time })}</span>
            )}
            <span>{t('voice.proposalConfidence', { pct: Math.round((proposal.data.confidence || 0) * 100) })}</span>
          </div>

          <div>
            <p className="text-xs font-medium text-surface-500 mb-1">
              {proposal.data.candidate_tickets.length > 0 ? t('voice.proposalCandidates') : t('voice.proposalNoCandidates')}
            </p>
            <div className="space-y-1">
              {proposal.data.candidate_tickets.map((ticket) => (
                <button
                  key={ticket.id}
                  type="button"
                  onClick={() => openDraft(proposal.data, ticket)}
                  className="w-full text-start px-3 py-2 rounded-lg bg-surface-0 border border-surface-200 hover:bg-primary-50 transition-colors text-sm flex items-center gap-2"
                >
                  <FileText size={14} className="text-accent-text shrink-0" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-surface-900">{ticket.title}</span>
                    <span className="block truncate text-xs text-surface-500">{ticket.organization_name}</span>
                  </span>
                  {ticket.assigned_to_me && (
                    <span className="shrink-0 rounded-full bg-primary-100 px-2 py-0.5 text-xs text-primary-700">
                      {t('voice.proposalAssignedToMe')}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn btn-secondary" onClick={() => openDraft(proposal.data)}>
              {t('voice.proposalChooseOther')}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setProposal(null)}>
              {t('voice.proposalDismiss')}
            </button>
          </div>
        </div>
      )}

      {draft && (
        <NewWorkLogModal
          initial={draft}
          onClose={() => setDraft(null)}
          onSuccess={() => {
            setDraft(null);
            setProposal(null);
            setConfirmed(true);
            queryClient.invalidateQueries({ queryKey: ['voice-entries'] });
          }}
        />
      )}

      {uploadMutation.isError && (
        <div className="card bg-danger-50 border border-danger-100">
          <div className="flex items-center gap-3">
            <XCircle className="text-danger-600" size={24} />
            <p className="text-danger-800" role="alert">{aiErrorText(uploadMutation.error, t('voice.aiNotConfigured'), t('voice.processError'))}</p>
          </div>
        </div>
      )}

      {/* Voice Entries List */}
      <h2 className="text-xl font-semibold text-surface-900">{t('voice.previousRecordings')}</h2>

      {isLoading && (
        <div className="card p-0">
          <StateBlock kind="loading" rows={3} />
        </div>
      )}

      {isError && !data && (
        <div className="card p-0">
          <StateBlock
            kind="error"
            title={t('states.voice.listError')}
            error={error}
            onRetry={() => void refetch()}
            retrying={isFetching}
          />
        </div>
      )}

      <div className="grid gap-4">
        {entries.length > 0 ? entries.map((entry) => {
          const config = statusConfig[entry.processing_status] || statusConfig.pending;
          const Icon = config.icon;
          return (
            <div key={entry.id} className="card hover:shadow-elevated transition-shadow">
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg ${config.color.split(' ')[1]}`}>
                  <Mic className={config.color.split(' ')[0]} size={24} />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      {entry.transcription_text ? (
                        <p className="text-surface-900">{entry.transcription_text.substring(0, 200)}{entry.transcription_text.length > 200 ? '...' : ''}</p>
                      ) : (
                        <p className="text-surface-500 italic">{t('voice.noTranscription')}</p>
                      )}
                    </div>
                    <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
                      <Icon size={16} className={entry.processing_status === 'processing' ? 'animate-spin' : ''} />
                      {config.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-surface-500 mt-2">
                    <span>{format(new Date(entry.created_at), 'dd/MM/yyyy HH:mm', { locale: he })}</span>
                    {entry.audio_duration_seconds && (
                      <><span>&bull;</span><span>{Math.round(entry.audio_duration_seconds)}s</span></>
                    )}
                    {entry.confidence && (
                      <><span>&bull;</span><span>{t('voice.confidence')}{(entry.confidence * 100).toFixed(0)}%</span></>
                    )}
                    {entry.work_log_id && (
                      <><span>&bull;</span><span className="text-success-700 font-medium">{t('voice.workLogLinked')}</span></>
                    )}
                    {!entry.work_log_id && entry.processing_status === 'processed' && (
                      <><span>&bull;</span><span className="text-primary-700 font-medium">{t('voice.proposalAwaiting')}</span></>
                    )}
                  </div>
                  {!entry.work_log_id && entry.processing_status === 'processed' && entry.agent_id === user?.id && (
                    <button
                      type="button"
                      className="btn btn-secondary text-xs mt-2"
                      onClick={() => setDraft(draftFromVoiceEntry(entry))}
                    >
                      {t('voice.proposalReview')}
                    </button>
                  )}
                  {entry.error_message && (
                    <p className="text-sm text-danger-700 mt-2">{entry.error_message}</p>
                  )}
                </div>
              </div>
            </div>
          );
        }) : !isLoading && !(isError && !data) && (
          <div className="card p-0">
            <StateBlock kind="empty" icon={Mic} title={t('voice.noPrevious')} />
          </div>
        )}
      </div>
    </div>
  );
}
